import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useGetCart } from '~/api';
import { Modal, ModalHeader } from '~/components/overlays';
import { Button } from '~/components/ui/buttons/Button';
import { LocationSelect } from '~/components/common/LocationSelect';
import { CartCatalogAddRow } from '~/pages/cart/sections/CartCatalogAddRow';
import {
  SelectSellerFacilityStep,
  type SelectedSellerFacility,
} from '~/components/common/SelectSellerFacilityStep';
import { useShipToLocations } from '~/components/common/useShipToLocations';
import { CartLines, type CartTarget } from './CartLines';
import { cartFor } from './cartFor';

type Step =
  | { kind: 'new' }
  | {
      kind: 'cart';
      target: CartTarget;
      label: string;
      sellerDefaultCurrency: string;
    };

type NewCartModalProps = {
  onClose: () => void;
};

function cartTargetLabel(
  seller: string,
  facility: string,
  shipTo: string
): string {
  return `${seller} — ${facility} → ${shipTo}`;
}

export function NewCartModal({ onClose }: Readonly<NewCartModalProps>) {
  const { t } = useTranslation('cart');
  const { data } = useGetCart(undefined, { withContacts: true });
  const carts = data?.carts ?? [];
  const [step, setStep] = useState<Step>({ kind: 'new' });
  const [facility, setFacility] = useState<SelectedSellerFacility | null>(null);
  const [locationId, setLocationId] = useState('');

  const backToNew = () => setStep({ kind: 'new' });

  const renderCart = ({
    target,
    sellerDefaultCurrency,
  }: Extract<Step, { kind: 'cart' }>) => {
    const cart = cartFor(carts, target);
    return (
      <>
        <CartCatalogAddRow
          key={`${target.facilityId}:${target.locationId}`}
          hasSuggestions={false}
          cartId={cart?.id ?? ''}
          cartItems={cart?.items ?? []}
          locationId={target.locationId}
          sellerOrganizationId={target.organizationId}
          sellerOrganizationFacilityId={target.facilityId}
          incoTerm={cart?.incoTerm}
          cartCurrency={cart?.currency}
          sellerDefaultCurrency={sellerDefaultCurrency}
          hasShippingRoute={!!cart?.shippingRoute}
        />
        {cart && <CartLines cart={cart} target={target} />}
        <Button
          variant="ghost"
          size="sm"
          width="auto"
          onClick={backToNew}
          data-testid="new-cart-change"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          {t('cart.new_cart.change')}
        </Button>
      </>
    );
  };

  return (
    <Modal isOpen onClose={onClose}>
      <div
        className="flex max-h-[92vh] w-[min(900px,94vw)] flex-col overflow-hidden rounded-2xl bg-neut-light"
        data-testid="new-cart-modal"
      >
        <ModalHeader
          title={t('cart.new_cart.title')}
          description={
            step.kind === 'cart' ? step.label : t('cart.new_cart.hint')
          }
          onClose={onClose}
        />
        {step.kind === 'cart' ? (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
            {renderCart(step)}
          </div>
        ) : (
          <NewCart
            facility={facility}
            onFacilityChange={setFacility}
            locationId={locationId}
            onLocationIdChange={setLocationId}
            onContinue={(facility, location) =>
              setStep({
                kind: 'cart',
                target: {
                  organizationId: facility.organizationId,
                  facilityId: facility.facilityId,
                  locationId: location.id,
                },
                sellerDefaultCurrency: facility.defaultCurrency,
                label: cartTargetLabel(
                  facility.organizationLabel,
                  facility.facilityLabel,
                  location.name
                ),
              })
            }
          />
        )}
      </div>
    </Modal>
  );
}

type ShipToLocation = ReturnType<
  typeof useShipToLocations
>['locations'][number];

function NewCart({
  facility,
  onFacilityChange,
  locationId,
  onLocationIdChange,
  onContinue,
}: Readonly<{
  facility: SelectedSellerFacility | null;
  onFacilityChange: (facility: SelectedSellerFacility) => void;
  locationId: string;
  onLocationIdChange: (locationId: string) => void;
  onContinue: (
    facility: SelectedSellerFacility,
    location: ShipToLocation
  ) => void;
}>) {
  const { t } = useTranslation('cart');
  const { locations, isLoading, error } = useShipToLocations();
  const location = locations.find((candidate) => candidate.id === locationId);

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <SelectSellerFacilityStep
          selectedFacilityId={facility?.facilityId ?? null}
          onSelectFacility={onFacilityChange}
        />
      </div>
      <div className="flex shrink-0 flex-wrap items-end gap-4 border-t border-neut-border bg-neut-50 px-6 py-4">
        <div className="w-full max-w-[30rem]">
          <LocationSelect
            label={t('cart.new_cart.ship_to')}
            name="shipTo"
            value={locationId}
            onChange={(_, value) => onLocationIdChange(value)}
            locations={locations}
            isLoading={isLoading}
            error={error}
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          width="auto"
          disabled={!facility || !location}
          onClick={() => facility && location && onContinue(facility, location)}
          data-testid="new-cart-continue"
        >
          {t('cart.new_cart.continue')}
        </Button>
      </div>
    </>
  );
}
